import { useCallback } from 'react';
import { GetMeQuery } from '@/generated/gql/graphql';
import { RecurringRule } from '@/enterprise/data/useRecurringRules';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import useUserDetails from '@/shared/data/useUserDetails';
import useAccountFeatureConfigs from '@/shared/providers/useAccountFeatureConfigs';
export interface Restrictions {
  canEditInvoices: boolean;
  canEditOffers: boolean;
  canEditRecurringRules: boolean;
}

/**
 * default restrictions
 */
const defaultRestrictions: Restrictions = {
  canEditInvoices: true,
  canEditOffers: true,
  canEditRecurringRules: true,
};

// invoices can only be edited if the division does not have recurring rules
const canEditInvoicesWithRecurringRules = (
  takerMarkets: TakerMarket[],
  recurringRulesFn: (takerMarkets: TakerMarket[]) => RecurringRule[]
) => {
  return takerMarkets.length !== recurringRulesFn(takerMarkets).length;
};

// returns true if all offers are non-preferred types
const areAllOffersNonPreferred = (takerMarkets: TakerMarket[]) => {
  return takerMarkets.every((takerMarket) => takerMarket.offerConfig.offerType !== 'PREFERRED_TERM');
};

const canEditRecurringRulesFn = (takerMarkets: TakerMarket[]) => {
  // immediately return if no taker markets are passed
  if (takerMarkets.length === 0) {
    return true;
  }

  // rules cannot be edited if division is preferred
  return areAllOffersNonPreferred(takerMarkets);
};

/**
 * used only for displaying read only banner
 * for determining user restrictions, use the useRestrictions hook
 */
export const useReadOnlyUser = () => {
  const { data: readOnlyUser } = useUserDetails(useCallback((user: GetMeQuery) => user.me?.supplierReadOnly, []));
  return readOnlyUser ?? false;
};

export const useSunspotUser = () => {
  const { data: segment } = useUserDetails(useCallback((user: GetMeQuery) => user.me?.account?.segment, []));
  return segment === 'Sun';
};

/**
 * used for determining if the user is a Sunspot user and has not been migrated from SMB ie. Enterprise Sunspot user
 */
export const useEnterpriseSunspotUser = () => {
  const isSunspotUser = useSunspotUser();
  const { wasSmbMigrated } = useAccountFeatureConfigs();
  return isSunspotUser && !wasSmbMigrated;
};

const useRestrictions = () => {
  const readOnlyUser = useReadOnlyUser();
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();

  const getRestrictions = useCallback(
    (takerMarkets: TakerMarket[]) => {
      return {
        ...defaultRestrictions,
        canEditInvoices:
          !readOnlyUser &&
          areAllOffersNonPreferred(takerMarkets) &&
          canEditInvoicesWithRecurringRules(takerMarkets, getRecurringRulesForTakerMarkets),
        canEditOffers: !readOnlyUser && areAllOffersNonPreferred(takerMarkets),
        canEditRecurringRules: !readOnlyUser && canEditRecurringRulesFn(takerMarkets),
      };
    },
    [getRecurringRulesForTakerMarkets, readOnlyUser]
  );

  return { getRestrictions };
};

export default useRestrictions;
